/**
 * Scroll positions of the reader's panes per reader path, so that Back from a Settings record (or any
 * other screen) returns to the list, the article and Details where they were (the prototype's
 * `readerReturn`). Kept in memory for the life of the tab.
 */

export type Pane = 'list' | 'reader' | 'details';

const memory = new Map<string, Partial<Record<Pane, number>>>();
const MAX = 50;

/** The list's scroll belongs to the scope; the article's and Details' to the message. */
export function scrollKey(pane: Pane, scopePath: string, messageId: string | null): string {
  return pane === 'list' ? `list:${scopePath}` : `${pane}:${scopePath}:${messageId ?? ''}`;
}

export function rememberScroll(key: string, pane: Pane, top: number): void {
  const entry = memory.get(key) ?? {};
  entry[pane] = top;
  memory.delete(key);
  memory.set(key, entry);
  if (memory.size > MAX) {
    const oldest = memory.keys().next().value;
    if (oldest !== undefined) memory.delete(oldest);
  }
}

export function recallScroll(key: string, pane: Pane): number | undefined {
  return memory.get(key)?.[pane];
}

/**
 * Restore a pane's remembered scroll position, and remember it while the person scrolls.
 *
 * The content of a pane can arrive in parts (Details: the message, then the catalogue, the per-model
 * scores and the install state, each a query of its own). Setting `scrollTop` on content that is still
 * too short is clamped by the browser, and the clamp fires a scroll event — which used to overwrite the
 * remembered position with the clamped one (reader.spec.ts "Details … Back restores", 150 → 0 under load).
 * So until the target is reached, a scroll below it is taken for a clamp: it is not remembered, and the
 * target is applied again whenever the content changes, for at most `RESTORE_MS`. A wheel, touch, pointer
 * or key in the pane is the person scrolling: the restore stops and their position is remembered.
 */
const RESTORE_MS = 3000;

export function restoreAndRememberScroll(el: HTMLElement, key: string, pane: Pane): () => void {
  let target = recallScroll(key, pane) ?? 0;
  let restoring = target > 0;
  el.scrollTop = target;
  if (el.scrollTop >= target - 1) restoring = false;
  let frame = 0;
  const stopRestoring = (): void => {
    restoring = false;
    observer?.disconnect();
    clearTimeout(timer);
  };
  const reapply = (): void => {
    if (!restoring) return;
    el.scrollTop = target;
    if (el.scrollTop >= target - 1) stopRestoring();
  };
  const onScroll = (): void => {
    if (restoring && el.scrollTop < target - 1) return; // a clamp, not the person
    if (restoring) stopRestoring();
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => rememberScroll(key, pane, el.scrollTop));
  };
  const onPerson = (): void => {
    if (restoring) stopRestoring();
    target = el.scrollTop;
  };
  const observer = restoring && typeof MutationObserver === 'function' ? new MutationObserver(reapply) : null;
  observer?.observe(el, { childList: true, subtree: true, characterData: true });
  const timer = setTimeout(stopRestoring, restoring ? RESTORE_MS : 0);
  const personEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
  el.addEventListener('scroll', onScroll, { passive: true });
  for (const type of personEvents) el.addEventListener(type, onPerson, { passive: true });
  return () => {
    cancelAnimationFrame(frame);
    observer?.disconnect();
    clearTimeout(timer);
    el.removeEventListener('scroll', onScroll);
    for (const type of personEvents) el.removeEventListener(type, onPerson);
  };
}
